var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { ErrorType } from "../types/ErrorType";
import { isValidIssueNumber } from "../utils/issueNumber";
import { extractServerErrorMessage, readErrorCode, } from "../utils/errorHandler";
import { useDebouncedValue } from "./useDebouncedValue";
import { useContinuousNumberingPreview } from "./useContinuousNumberingPreview";
var START_DEBOUNCE_MS = 400;
var DEFAULT_START = 1;
var KEEP_PER_VOLUME = "KEEP_PER_VOLUME";
var parseStartNumber = function (input) {
    return isValidIssueNumber(input) ? Number(input.trim()) : null;
};
// A title switched on before it had any volume has no anchor yet: saving anchors it, as enabling does.
var intendedAction = function (continuous, anchored) {
    if (!continuous)
        return "DISABLE";
    return anchored ? "CHANGE_START" : "ENABLE";
};
/**
 * Holds the numbering part of the check settings dialog: what the user wants, the backend preview
 * of it and the request that may be saved - only the one the shown, settled preview was computed for.
 */
export var useContinuousNumberingDraft = function (settings, checkSettings) {
    var _a, _b, _c;
    var formatMessage = useIntl().formatMessage;
    var stored = settings.continuousNumbering;
    var storedStart = (_a = settings.continuousNumberingStartNumber) !== null && _a !== void 0 ? _a : null;
    var anchored = stored && storedStart !== null;
    var _d = useState(stored), continuous = _d[0], setContinuousState = _d[1];
    var _e = useState(String((_b = (anchored ? storedStart : settings.suggestedContinuousStartNumber)) !== null && _b !== void 0 ? _b : DEFAULT_START)), startInput = _e[0], setStartInputState = _e[1];
    var _f = useState(KEEP_PER_VOLUME), pastVolumes = _f[0], setPastVolumesState = _f[1];
    var _g = useState("RESET"), disableMode = _g[0], setDisableModeState = _g[1];
    var _h = useState(null), previewError = _h[0], setPreviewError = _h[1];
    var _j = useState(false), planStale = _j[0], setPlanStale = _j[1];
    var startNumber = parseStartNumber(startInput);
    var debouncedStart = parseStartNumber(useDebouncedValue(startInput, START_DEBOUNCE_MS));
    var startValid = !continuous || startNumber !== null;
    var changed = continuous !== stored || (continuous && startNumber !== storedStart);
    var action = intendedAction(continuous, anchored);
    var request = useMemo(function () {
        if (!changed)
            return null;
        var withSettings = checkSettings ? { checkSettings: checkSettings } : {};
        if (!continuous)
            return __assign({ enabled: false, disableMode: disableMode }, withSettings);
        if (debouncedStart === null)
            return null;
        return anchored
            ? __assign({ enabled: true, startNumber: debouncedStart }, withSettings) : __assign({ enabled: true, startNumber: debouncedStart, pastVolumes: pastVolumes }, withSettings);
    }, [
        changed,
        continuous,
        disableMode,
        debouncedStart,
        anchored,
        pastVolumes,
        checkSettings,
    ]);
    var previewQuery = useContinuousNumberingPreview(settings.titleId, request, {
        onSuccess: function (preview) {
            setPreviewError(null);
            if (!preview.renumberBackwardAvailable) {
                setPastVolumesState(KEEP_PER_VOLUME);
            }
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var _a;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, readErrorCode(error)];
                    case 1:
                        if ((_b.sent()) ===
                            ErrorType.CONTINUOUS_NUMBERING_BACKWARD_BELOW_ONE) {
                            setPastVolumesState(KEEP_PER_VOLUME);
                            return [2 /*return*/];
                        }
                        _a = setPreviewError;
                        return [4 /*yield*/, extractServerErrorMessage(error, formatMessage, formatMessage({
                                id: "ui-br-legaldeposits.periodicalCheck.settings.numbering.previewError",
                            }))];
                    case 2:
                        _a.apply(void 0, [_b.sent()]);
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var data = previewQuery.data, isError = previewQuery.isError, isFetching = previewQuery.isFetching, isPreviousData = previewQuery.isPreviousData, refetch = previewQuery.refetch;
    var preview = changed && startValid && !isError && (data === null || data === void 0 ? void 0 : data.action) === action ? data : null;
    var settled = request !== null &&
        !isError &&
        !isFetching &&
        !isPreviousData &&
        (!continuous || request.startNumber === startNumber);
    var planPreview = changed && settled ? (data !== null && data !== void 0 ? data : null) : null;
    var ready = !changed || planPreview !== null;
    var loading = changed && startValid && !isError && !ready;
    var saveRequest = useMemo(function () {
        return request && planPreview && planPreview.action !== "NONE"
            ? __assign(__assign({}, request), { planToken: planPreview.planToken }) : null;
    }, [request, planPreview]);
    var setContinuous = useCallback(function (value) {
        setPlanStale(false);
        setContinuousState(value);
    }, []);
    var setStartInput = useCallback(function (value) {
        setPlanStale(false);
        setStartInputState(value);
    }, []);
    var setPastVolumes = useCallback(function (value) {
        setPlanStale(false);
        setPastVolumesState(value);
    }, []);
    var setDisableMode = useCallback(function (value) {
        setPlanStale(false);
        setDisableModeState(value);
    }, []);
    var markPlanStale = useCallback(function () {
        setPlanStale(true);
        refetch();
    }, [refetch]);
    return {
        stored: stored,
        storedStart: storedStart,
        continuous: continuous,
        setContinuous: setContinuous,
        startInput: startInput,
        setStartInput: setStartInput,
        startNumber: startNumber,
        startValid: startValid,
        pastVolumes: pastVolumes,
        setPastVolumes: setPastVolumes,
        disableMode: disableMode,
        setDisableMode: setDisableMode,
        changed: changed,
        action: action,
        preview: preview,
        loading: loading,
        ready: ready,
        previewError: previewError,
        planStale: planStale,
        markPlanStale: markPlanStale,
        saveRequest: saveRequest,
        renumbers: (_c = preview === null || preview === void 0 ? void 0 : preview.renumbers) !== null && _c !== void 0 ? _c : false,
    };
};
